import React, { useState } from 'react';
import { digitsOnly, MoneyField } from './persianMoney.jsx';
import { X, MapPin, Pencil, Trash2 } from 'lucide-react';
import LocationPicker from './locationPicker.jsx';
import './searchFilter.scss';

const DEAL_TYPES = [
  { id: 'sale', label: 'خرید و فروش' },
  { id: 'rent', label: 'اجاره' },
];

const ROOM_OPTIONS = ['۱', '۲', '۳', '۴', '۵+'];

const DOCUMENT_OPTIONS = [
  { id: 'single-page', label: 'تک‌برگ' },
  { id: 'poa', label: 'وکالتی' },
  { id: 'splitting', label: 'در حال تفکیک' },
];

const initialFilters = {
  priceFrom: '',
  priceTo: '',
  depositFrom: '',
  depositTo: '',
  rentFrom: '',
  rentTo: '',
  area: { from: '', to: '' },
  buildYear: { from: '', to: '' },
  floor: { from: '', to: '' },
  rooms: [],
  document: null,
  hasElevator: false,
  hasParking: false,
  hasStorage: false,
  petsAllowed: false,
  singleTenant: false,
  ownerInBuilding: false,
  convertibleToFullDeposit: false,
  region: null, // محدوده‌ای که کاربر روی نقشه خط‌کشی کرده (GeoJSON Feature) — null = کلِ استان
};

function toggleArrayItem(arr, item) {
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
}

function RangeField({ label, unit, from, to, onChangeFrom, onChangeTo }) {
  // مبلغ‌ها: سه‌رقم‌سه‌رقم و زیرِ هر فیلد همان مبلغ با حروف (برای راحتیِ کاربر)
  if (unit === 'تومان') {
    return (
      <div className="search-filter__field">
        <span className="search-filter__label">{label}</span>
        <div className="search-filter__range search-filter__range--money">
          <MoneyField placeholder="از" value={from} onChange={onChangeFrom} />
          <span className="search-filter__range-sep">تا</span>
          <MoneyField placeholder="تا" value={to} onChange={onChangeTo} />
        </div>
      </div>
    );
  }
  return (
    <div className="search-filter__field">
      <span className="search-filter__label">{label}</span>
      <div className="search-filter__range">
        <input
          type="text"
          inputMode="numeric"
          placeholder="از"
          value={from}
          onChange={(e) => onChangeFrom(digitsOnly(e.target.value))}
        />
        <span className="search-filter__range-sep">تا</span>
        <input
          type="text"
          inputMode="numeric"
          placeholder="تا"
          value={to}
          onChange={(e) => onChangeTo(digitsOnly(e.target.value))}
        />
        {unit && <span className="search-filter__unit">{unit}</span>}
      </div>
    </div>
  );
}

function ToggleField({ label, checked, onChange }) {
  return (
    <button
      type="button"
      className={`search-filter__toggle ${checked ? 'is-on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span>{label}</span>
      <span className="search-filter__toggle-track">
        <span className="search-filter__toggle-thumb" />
      </span>
    </button>
  );
}

// initial: فیلترهای اعمال‌شده‌ی قبلی ({ dealType, filters }) تا با باز کردنِ دوباره همان‌ها دیده شوند
function SearchFilter({ onClose, onApply, initial, province }) {
  const [mapOpen, setMapOpen] = useState(false);
  // «آگهیِ جدید در این محدوده آمد، خبرم کن» (فقط با محدوده‌ی روی نقشه) — با «نمایش نتایج» ساخته می‌شود
  const [alertOn, setAlertOn] = useState(false);
  const [alertName, setAlertName] = useState('');
  const [dealType, setDealType] = useState((initial && initial.dealType) || 'sale');
  const [filters, setFilters] = useState(() => ({ ...initialFilters, ...((initial && initial.filters) || {}) }));

  const update = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const updateRange = (key, part, value) =>
    setFilters((prev) => ({ ...prev, [key]: { ...prev[key], [part]: value } }));

  const handleReset = () => setFilters(initialFilters);

  const handleApply = () => {
    onApply?.({ dealType, filters, alert: alertOn && filters.region ? { name: alertName.trim() } : null });
  };

  return (
    <div className="search-filter">
      <div className="search-filter__backdrop" onClick={onClose} />
      <div className="search-filter__sheet">
        <div className="search-filter__header">
          <h2>فیلترها</h2>
          <button type="button" className="search-filter__close" onClick={onClose} aria-label="بستن">
            <X size={20} strokeWidth={1.8} />
          </button>
        </div>

        <div className="search-filter__deal-type">
          {DEAL_TYPES.map((d) => (
            <button
              key={d.id}
              type="button"
              className={dealType === d.id ? 'is-active' : ''}
              onClick={() => setDealType(d.id)}
            >
              {d.label}
            </button>
          ))}
        </div>

        <div className="search-filter__body">
          {dealType === 'sale' && (
            <RangeField
              label="قیمت"
              unit="تومان"
              from={filters.priceFrom}
              to={filters.priceTo}
              onChangeFrom={(v) => update('priceFrom', v)}
              onChangeTo={(v) => update('priceTo', v)}
            />
          )}

          {dealType === 'rent' && (
            <>
              <RangeField
                label="ودیعه"
                unit="تومان"
                from={filters.depositFrom}
                to={filters.depositTo}
                onChangeFrom={(v) => update('depositFrom', v)}
                onChangeTo={(v) => update('depositTo', v)}
              />
              <RangeField
                label="اجاره"
                unit="تومان"
                from={filters.rentFrom}
                to={filters.rentTo}
                onChangeFrom={(v) => update('rentFrom', v)}
                onChangeTo={(v) => update('rentTo', v)}
              />
            </>
          )}

          <div className="search-filter__field">
            <span className="search-filter__label">محدوده روی نقشه</span>
            {filters.region ? (
              <div className="search-filter__region">
                <span className="search-filter__region-text">
                  <MapPin size={16} strokeWidth={2} />
                  محدوده انتخاب شد
                </span>
                <button type="button" onClick={() => setMapOpen(true)} aria-label="ویرایش محدوده">
                  <Pencil size={15} strokeWidth={2} />
                  ویرایش
                </button>
                <button type="button" className="is-danger" onClick={() => update('region', null)} aria-label="حذف محدوده">
                  <Trash2 size={15} strokeWidth={2} />
                </button>
              </div>
            ) : null}
            {filters.region ? (
              <div className={`search-filter__alert ${alertOn ? 'is-on' : ''}`}>
                <ToggleField label="آگهیِ جدید در این محدوده آمد، خبرم کن (پیامک)" checked={alertOn} onChange={setAlertOn} />
                {alertOn && (
                  <input
                    type="text"
                    className="search-filter__alert-name"
                    maxLength={60}
                    placeholder="یک نام برای این محدوده (مثلاً «نزدیکِ محلِ کار»)"
                    value={alertName}
                    onChange={(e) => setAlertName(e.target.value)}
                  />
                )}
              </div>
            ) : (
              <button type="button" className="search-filter__location-btn" onClick={() => setMapOpen(true)}>
                <MapPin size={16} strokeWidth={2} />
                {province ? `انتخاب محدوده روی نقشه‌ی ${province}` : 'انتخاب محدوده روی نقشه'}
              </button>
            )}
          </div>

          <RangeField
            label="سن بنا"
            unit="سال"
            from={filters.buildYear.from}
            to={filters.buildYear.to}
            onChangeFrom={(v) => updateRange('buildYear', 'from', v)}
            onChangeTo={(v) => updateRange('buildYear', 'to', v)}
          />

          <RangeField
            label="متراژ"
            unit="متر"
            from={filters.area.from}
            to={filters.area.to}
            onChangeFrom={(v) => updateRange('area', 'from', v)}
            onChangeTo={(v) => updateRange('area', 'to', v)}
          />

          {dealType === 'sale' && (
            <RangeField
              label="طبقه"
              from={filters.floor.from}
              to={filters.floor.to}
              onChangeFrom={(v) => updateRange('floor', 'from', v)}
              onChangeTo={(v) => updateRange('floor', 'to', v)}
            />
          )}

          <div className="search-filter__field">
            <span className="search-filter__label">تعداد اتاق</span>
            <div className="search-filter__chips">
              {ROOM_OPTIONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  className={`search-filter__chip ${filters.rooms.includes(r) ? 'is-selected' : ''}`}
                  onClick={() => update('rooms', toggleArrayItem(filters.rooms, r))}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {dealType === 'sale' && (
            <div className="search-filter__field">
              <span className="search-filter__label">وضعیت سند</span>
              <div className="search-filter__chips">
                {DOCUMENT_OPTIONS.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className={`search-filter__chip ${filters.document === d.id ? 'is-selected' : ''}`}
                    onClick={() => update('document', filters.document === d.id ? null : d.id)}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="search-filter__toggles">
            <ToggleField
              label="آسانسور دارد"
              checked={filters.hasElevator}
              onChange={(v) => update('hasElevator', v)}
            />
            <ToggleField
              label="پارکینگ دارد"
              checked={filters.hasParking}
              onChange={(v) => update('hasParking', v)}
            />
            <ToggleField
              label="انباری دارد"
              checked={filters.hasStorage}
              onChange={(v) => update('hasStorage', v)}
            />

            {dealType === 'rent' && (
              <>
                <ToggleField
                  label="حیوان خانگی"
                  checked={filters.petsAllowed}
                  onChange={(v) => update('petsAllowed', v)}
                />
                <ToggleField
                  label="مجرد"
                  checked={filters.singleTenant}
                  onChange={(v) => update('singleTenant', v)}
                />
                <ToggleField
                  label="مالک داخل ساختمان است"
                  checked={filters.ownerInBuilding}
                  onChange={(v) => update('ownerInBuilding', v)}
                />
                <ToggleField
                  label="قابل تبدیل به رهن کامل"
                  checked={filters.convertibleToFullDeposit}
                  onChange={(v) => update('convertibleToFullDeposit', v)}
                />
              </>
            )}
          </div>
        </div>

        <div className="search-filter__footer">
          <button type="button" className="search-filter__reset" onClick={handleReset}>
            پاک کردن فیلترها
          </button>
          <button type="button" className="search-filter__apply" onClick={handleApply}>
            نمایش نتایج
          </button>
        </div>
      </div>
      {mapOpen && (
        <LocationPicker
          initialProvince={province}
          initialShape={filters.region}
          onClose={() => setMapOpen(false)}
          onConfirm={(geo) => update('region', geo)}
        />
      )}
    </div>
  );
}

export default SearchFilter;